import type { IRecord, IRecordTypeDefinition } from '@platform/core/types';
import { isDoneStatus } from '@platform/engine/views/doneStatuses';
import type { IAnalytics } from './IAnalytics';
import type { IAnalyticsBar } from './IAnalyticsBar';
import type { IAnalyticsFields } from './IAnalyticsFields';

const DAY = 86_400_000;
const WEEKS = 8;
const TOP = 6;

const text = (v: unknown): string => (typeof v === 'string' ? v : v && typeof v === 'object' && 'id' in v ? String((v as { id: unknown }).id) : '');
const nameOf = (v: unknown): string => (v && typeof v === 'object' && 'name' in v ? String((v as { name: unknown }).name) : '');
const isoDay = (ms: number): string => new Date(ms).toISOString().slice(0, 10);

/** Відра за ключем у порядку `order` (кожен ключ, навіть із нулем). */
function countIn(records: IRecord[], keyOf: (r: IRecord) => string, order: readonly string[]): IAnalyticsBar[] {
  const counts = new Map<string, number>();
  for (const r of records) counts.set(keyOf(r), (counts.get(keyOf(r)) ?? 0) + 1);
  return order.map((key) => ({ key, count: counts.get(key) ?? 0 }));
}

/**
 * Аналітика модуля за правилами прототипу v4 (isDashboard): записи, відкриті, прострочені (відкриті з терміном до сьогодні), виконані,
 * сума залишку по відкритих; статуси — усі записи; виконавці (шість найбільших) і пріоритети — лише відкриті; вісім тижнів створених
 * і завершених (завершені записи за часом останньої зміни); терміни до сьогодні + 7 днів. «Завершений» — статус із глобального DONE
 * прототипу (isDone), а не прапорець closed модуля: аналітика прототипу рахує саме так («Підтверджено» — відкритий). Записи — у порядку
 * масиву прототипу (за номером): від нього залежать рівні частки виконавців і терміни з однаковою датою. Чиста функція: `now` — час у мс.
 */
export function analyticsOf(all: IRecord[], rt: IRecordTypeDefinition, fields: IAnalyticsFields, now: number): IAnalytics {
  const today = isoDay(now);
  const in7 = isoDay(now + 7 * DAY);
  const records = [...all].sort((a, b) => a.code - b.code);
  const open = records.filter((r) => !isDoneStatus(r.status));
  const dueOf = (r: IRecord): string => (fields.due ? text(r.values[fields.due]).slice(0, 10) : '');
  const overdue = open.filter((r) => dueOf(r) && dueOf(r) < today);
  const who = (r: IRecord): unknown => (fields.assignee ? r.values[fields.assignee] : undefined);
  const names = new Map(open.map((r) => [text(who(r)), nameOf(who(r))]));
  const byUser = countIn(open, (r) => text(who(r)), [...names.keys()]).map((b) => ({ ...b, label: names.get(b.key) || undefined }));
  const priority = fields.priority ? rt.fields.find((f) => f.key === fields.priority) : undefined;
  const weeks = Array.from({ length: WEEKS }, (_, i) => {
    const ago = WEEKS - 1 - i;
    const end = now - ago * 7 * DAY;
    const start = end - 7 * DAY;
    const within = (iso: string) => { const t = Date.parse(iso); return t >= start && t < end; };
    return { ago, created: records.filter((r) => within(r.createdAt)).length, completed: records.filter((r) => isDoneStatus(r.status) && within(r.updatedAt)).length };
  });
  return {
    total: records.length,
    open: open.length,
    overdue: overdue.length,
    done: records.length - open.length,
    remaining: fields.remaining ? open.reduce((sum, r) => sum + (Number(r.values[fields.remaining as string]) || 0), 0) : undefined,
    byStatus: countIn(records, (r) => r.status, rt.statuses.list.map((s) => s.value)),
    byAssignee: byUser.filter((b) => b.count > 0).sort((a, b) => b.count - a.count).slice(0, TOP),
    byPriority: priority ? countIn(open, (r) => text(r.values[priority.key]), (priority.options ?? []).map((o) => o.value)) : undefined,
    weeks,
    due: open.filter((r) => dueOf(r) && dueOf(r) <= in7).sort((a, b) => dueOf(a).localeCompare(dueOf(b))).slice(0, TOP),
  };
}
